import { auth, usersSupportNode } from '@/api/firebase'
import firebase from 'firebase/app'
import { USERS_SUPPORT } from 'db'
import { router } from '@/main'

const state = {
    currentUser: false
}

const getters = {
    GET_CURRENT_USER: (state) => state.currentUser
}

const mutations = {
    SET_CURRENT_USER(state, payload) {
        state.currentUser = payload
    },
    CLEAR_CURRENT_USER(state) {
        state.currentUser = false
    }
}

const actions = {
    // =================================== Auth Operations ===================================

    /**
     * SET_AUTH
     *
     *     Check user and set auth.
     *
     * @returns {Promise<void>}
     */
    async SET_AUTH({ commit }, payload) {
        const idTokenResult = await auth.currentUser.getIdTokenResult()
        if (idTokenResult.claims.support === true) {
            if (!state.currentUser) commit('SET_CURRENT_USER', auth.currentUser.toJSON())
            // Define an INIT if sockets need to be established on app boot
            // await dispatch('INIT', {})
            if (!payload.refresh) {
                router.replace('/portal/dashboard')
            }
        } else {
            await auth.signOut()
            router.replace('/login')
            const authError = new Error()
            authError.code = 'auth/unauthorized'
            throw authError
        }
    },

    /**
     * LOGIN
     *
     *     Authenticate user and set if claim is courier, otherwise, throw an error.
     *
     * @param payload.email {string} - The email for the user account.
     * @param payload.password {string} - The password for the user account.
     * @param payload.rememberMe {boolean} - persistence setting
     * @param payload.login {func} - Router call to dispatch inside a successful Init callback.
     * @returns {Promise<void>}
     */
    async LOGIN({ dispatch }, payload) {
        if (payload.rememberMe) {
            await auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL)
        } else {
            await auth.setPersistence(firebase.auth.Auth.Persistence.SESSION)
        }
        await auth.signInWithEmailAndPassword(payload.email, payload.password)
        await dispatch('SET_AUTH', {
            refresh: false
        })
    },

    /**
     * UPDATE_PROFILE
     *
     *     Update profile information.
     *
     * @param payload.changeEmail {boolean} - Determine whether to change the email.
     * @param payload.email {string} - The email of the user.
     * @param payload.firstName {string} - The first name of the user.
     * @param payload.lastName {string} - The last name of the user.
     * @param payload.password {string} - Reauthenticate if changing email.
     * @returns {Promise<void>}
     */
    async UPDATE_PROFILE(context, payload) {
        if (payload.changeEmail) {
            const credentials = firebase.auth.EmailAuthProvider.credential(auth.currentUser.email, payload.password)
            await auth.currentUser.reauthenticateAndRetrieveDataWithCredential(credentials)
            await auth.currentUser.updateEmail(payload.email)
        }
        await usersSupportNode.child(auth.currentUser.uid).update({
            [USERS_SUPPORT.fields.firstName]: payload.firstName || null,
            [USERS_SUPPORT.fields.lastName]: payload.lastName || null,
            [USERS_SUPPORT.fields.email]: payload.email
        })
    },

    /**
     * UPDATE_PASSWORD
     *
     *     Reauthenticate with current password then update the password.
     *
     * @param payload.currentPassword {string} - The current password of the user.
     * @param payload.password {string} - The password to change to.
     * @returns {Promise<void>}
     */
    async UPDATE_PASSWORD(_, payload) {
        const credentials = firebase.auth.EmailAuthProvider.credential(auth.currentUser.email, payload.currentPassword)
        await auth.currentUser.reauthenticateAndRetrieveDataWithCredential(credentials)
        await auth.currentUser.updatePassword(payload.password)
    },

    /**
     * LOGOUT
     *
     *     Logs out the current user.
     *
     * @returns {Promise<void>}
     */
    async LOGOUT({ commit }) {
        await auth.signOut()
        commit('CLEAR_CURRENT_USER')
    },

    /**
     * FORGOT_PASSWORD
     *
     *     Request an email to be sent for password reset.
     *
     * @param payload.email {string} - The email of the user account requesting a password reset.
     * @returns {Promise<void>}
     */
    async FORGOT_PASSWORD(_, payload) {
        await auth.sendPasswordResetEmail(payload.email)
    }

    // =================================== Read Operations ===================================

    // =================================== Listener Operations ===================================
}

export { state, mutations, getters, actions }
